import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { TrendingUp, Users, Lightbulb, Target, BarChart2 } from 'lucide-react';
import { GlassCard, GlassTabs, ModuleHeader } from '../ui';
import TrendChart from './TrendChart';
import LeadScoreTable from './LeadScoreTable';
import InsightCard from './InsightCard';
import PredictionDashboardWidget from './PredictionDashboardWidget';
import './PredictionsModule.css';

interface OutletCtx {
    selectedProjectId?: string;
}

const TABS = [
    { id: 'trends', label: 'Trends', icon: <TrendingUp size={16} /> },
    { id: 'leads', label: 'Lead Scoring', icon: <Users size={16} /> },
    { id: 'insights', label: 'Insights', icon: <Lightbulb size={16} /> },
    { id: 'accuracy', label: 'Accuracy', icon: <Target size={16} /> },
];

const PredictionsModule: React.FC = () => {
    const { selectedProjectId } = (useOutletContext<OutletCtx>() || {});
    const [activeTab, setActiveTab] = useState('trends');

    if (!selectedProjectId) {
        return (
            <div className="predictions-module">
                <ModuleHeader title="Predictive Analytics" breadcrumb={['Analytics', 'Predictions']} />
                <div className="predictions-empty-state">
                    <GlassCard padding="lg">
                        <div className="predictions-empty-content">
                            <BarChart2 size={48} style={{ color: 'var(--text-muted)' }} />
                            <h2 style={{ color: 'var(--text)', margin: '16px 0 8px', fontWeight: 'var(--font-medium)' }}>
                                Select a Project
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                                Choose a project from the sidebar to view predictive analytics, trend forecasts, and lead scoring.
                            </p>
                        </div>
                    </GlassCard>
                </div>
            </div>
        );
    }

    return (
        <div className="predictions-module">
            <ModuleHeader title="Predictive Analytics" breadcrumb={['Analytics', 'Predictions']} />

            <div className="predictions-tabs-wrapper">
                <GlassTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            <div className="predictions-content">
                {activeTab === 'trends' && (
                    <TrendChart projectId={selectedProjectId} />
                )}
                {activeTab === 'leads' && (
                    <LeadScoreTable projectId={selectedProjectId} />
                )}
                {activeTab === 'insights' && (
                    <InsightCard projectId={selectedProjectId} />
                )}
                {activeTab === 'accuracy' && (
                    <div className="predictions-accuracy-tab">
                        <PredictionDashboardWidget projectId={selectedProjectId} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PredictionsModule;
